import React, { useEffect, useState, useContext } from 'react';
import { StoreContext } from '../context/StoreContext';
import './Orders.css';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../utils/formatPrice';
// ✅ UPDATE: Import API_BASE_URL
import { getUserOrders, getGuestOrders, cancelOrder, API_BASE_URL } from '../api/apiService';
import { toast } from 'react-toastify';

const statusIcons = {
  placed: '🟢',
  accepted: '✅',
  preparing: '👨‍🍳',
  ready_to_pickup: '📦',
  out_for_delivery: '🛵',
  delivered: '🎉',
  cancelled: '❌',
  rejected: '❌',
};

const statusColors = {
  placed: '#3498db',
  accepted: '#2980b9',
  preparing: '#f39c12',
  ready_to_pickup: '#1abc9c',
  out_for_delivery: '#e67e22',
  delivered: '#27ae60',
  cancelled: '#e74c3c',
  rejected: '#c0392b',
  scheduled: '#8e44ad', // Purple
};

const statusLabels = {
  placed: 'Order Placed',
  accepted: 'Accepted',
  preparing: 'Preparing',
  ready_to_pickup: 'Ready to Pickup',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

const Orders = () => {
  // ✅ REMOVED 'url' from context as we use API_BASE_URL now
  const { user, guestEmail, guestVerified } = useContext(StoreContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State to track which order is asking for confirmation
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        let response;
        if (user) {
          response = await getUserOrders();
        } else if (guestVerified && guestEmail) {
          response = await getGuestOrders(guestEmail);
        } else {
          setError('Please log in or verify as a guest to view your orders.');
          setLoading(false);
          return;
        }

        if (response.data.success) {
          setOrders(response.data.data || []);
          if (!response.data.data || response.data.data.length === 0) {
            setError('No orders found.');
          }
        } else {
          setError(response.data.message || 'Failed to fetch orders.');
        }
      } catch (err) {
        setError('An error occurred while fetching your orders.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [user, guestEmail, guestVerified]);

  const confirmCancelOrder = async (orderId) => {
    try {
      const response = await cancelOrder(orderId);
      if (response.data.success) {
        setOrders(prevOrders =>
          prevOrders.map(o => o._id === orderId ? { ...o, status: 'cancelled' } : o)
        );
      } else {
        toast.error(response.data.message || "Failed to cancel order.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error cancelling order.");
    } finally {
      setCancellingOrderId(null);
    }
  };

  const handleTrackProgress = (orderId) => {
    navigate(`/track-order/${orderId}`);
  };

  // ✅ FIXED: Robust Image URL Helper using API_BASE_URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://placehold.co/100?text=No+Image';
    if (imagePath.startsWith('http')) return imagePath;
    
    // Remove leading slashes or 'uploads/' to avoid duplication
    const cleanPath = imagePath.replace(/^(\/|uploads\/)+/, '');
    
    // Construct valid URL: http://localhost:5000/uploads/filename.jpg
    return `${API_BASE_URL}/uploads/${cleanPath}`;
  };

  // Smart Date Label Helper
  const getScheduledLabel = (order) => {
    if (order.status !== 'scheduled') {
      return statusLabels[order.status] || order.status;
    }
    const scheduledDate = new Date(order.scheduledFor);
    const today = new Date();
    
    const isToday = scheduledDate.getDate() === today.getDate() &&
                    scheduledDate.getMonth() === today.getMonth() &&
                    scheduledDate.getFullYear() === today.getFullYear();

    return isToday ? "Scheduled for Today" : "Scheduled for Tomorrow";
  };

  if (loading) {
    return (
      <div className="orders-loading-viewport">
        <div className="loading-state">
          <div className="loading-content">
            <div className="loading-icon">🛍️</div>
            <div className="loading-shimmer"></div>
            <div style={{ marginTop: 18, fontWeight: 600, color: "#27ae60" }}>Loading your orders...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <h2 className="orders-title">My Orders</h2>
      {orders.length === 0 ? (
        <div className="empty-orders">
          <p>{error || 'You have not placed any orders yet.'}</p>
          <button className="track-btn" onClick={() => navigate('/')}>Start Shopping</button>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map(order => (
            <div className="order-card modern-card" key={order._id}>
              <div className="order-header">
                <span className="order-id"><b>Order ID:</b> {order._id}</span>
                {order.vendor_name && (<span className="order-vendor">| {order.vendor_name}</span>)}
                <span
                  className={`order-status ${order.status}`}
                  style={{
                    background: statusColors[order.status] || '#888',
                    color: '#fff',
                    borderRadius: 16,
                    padding: '4px 16px',
                    fontWeight: 600,
                    fontSize: '1em',
                    marginLeft: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <span style={{ fontSize: '1.1em', marginRight: 4 }}>
                    {statusIcons[order.status] || '🟢'}
                  </span>
                  {getScheduledLabel(order)}
                </span>
                
                 {/* Check if order is scheduled AND not in a finished state */}
                {order.isScheduledOrder && !['delivered', 'cancelled', 'rejected'].includes(order.status) && (
                   <div className="scheduled-badge">
                      {getScheduledLabel(order) === "Scheduled for Today" ? '🕒 ' : '🌙 '} {getScheduledLabel(order)}
                      <br/>
                      <small style={{fontSize: '0.85em', opacity: 0.9}}>
                        Expected: {new Date(order.scheduledFor).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </small>
                   </div>
                )}

              </div>
              <div className="order-details">
                <div className="order-items">
                  {order.items.map(item => {
                    const discount = item.discount_applied || 0;
                    const discountedUnit = Math.round((item.price_per_unit - (item.price_per_unit * discount / 100)) * 100) / 100;
                    const originalTotal = (item.price_per_unit * item.quantity).toFixed(2);
                    const discountedTotal = (discountedUnit * item.quantity).toFixed(2);
                    return (
                      <div className="order-item modern-item-row" key={item._id || item.item_id}>
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.name}
                          className="order-item-img"
                          // Loop protection: if placeholder also fails, stop.
                          onError={(e) => {
                            if (e.target.src !== 'https://placehold.co/100?text=No+Image') {
                               e.target.src = 'https://placehold.co/100?text=No+Image'; 
                            }
                          }}
                        />
                        <div className="order-item-info">
                          <div className="order-item-name">
                            {item.name}
                            {item.spicyLevel && item.spicyLevel.trim() && (
                              <span style={{ color: '#333', marginLeft: 8, fontWeight: 'bold', fontSize: '0.9em', textTransform: 'capitalize' }}>
                                | {item.spicyLevel.trim()}
                              </span>
                            )}
                          </div>
                          <div className="order-item-qty">x{item.quantity}</div>
                          <div className="order-item-price">
                            {discount > 0 ? (
                              <>
                                <span className="order-item-original">{formatPrice(originalTotal, order.currency, order.currencySymbol)}</span>
                                <span className="order-item-discounted">{formatPrice(discountedTotal, order.currency, order.currencySymbol)}</span>
                              </>
                            ) : (
                              <span>{formatPrice(originalTotal, order.currency, order.currencySymbol)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="order-meta">
                  <div>
                    <span role="img" aria-label="calendar" style={{ marginRight: 4 }}>📅</span>
                    <b>Date:</b> {new Date(order.createdAt).toLocaleString()}
                  </div>
                  <div>
                    <span role="img" aria-label="payment" style={{ marginRight: 4 }}>💳</span>
                    <b>Payment:</b>{' '}
                    {order.payment_mode && order.payment_mode !== 'COD'
                      ? `${order.payment_mode} (${order.payment_status})`
                      : `Online Payment (${order.payment_status})`}
                  </div>
                  <div>
                    <span role="img" aria-label="location" style={{ marginRight: 4 }}>📍</span>
                    <b>Delivery:</b> {
                      [
                        order.delivery_address_snapshot?.door_number,
                        order.delivery_address_snapshot?.street_name || order.delivery_address_snapshot?.street,
                        order.delivery_address_snapshot?.locality,
                        order.delivery_address_snapshot?.city,
                        order.delivery_address_snapshot?.state,
                        order.delivery_address_snapshot?.zip_code,
                        order.delivery_address_snapshot?.country
                      ].filter(Boolean).join(', ')
                    }
                  </div>
                  <div>
                    <span role="img" aria-label="total" style={{ marginRight: 4 }}>🧾</span>
                    <b>Total:</b> <span style={{ color: '#27ae60', fontWeight: 600 }}>
                      {formatPrice(order.net_amount, order.currency, order.currencySymbol)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="order-actions">
                {order.status === 'scheduled' && (
                  <>
                    {cancellingOrderId === order._id ? (
                      <div className="cancel-confirm-box">
                        <span className="cancel-confirm-text">Cancel Order?</span>
                        <button className="btn-confirm-yes" onClick={() => confirmCancelOrder(order._id)}>Yes</button>
                        <button className="btn-confirm-no" onClick={() => setCancellingOrderId(null)}>No</button>
                      </div>
                    ) : (
                      <button 
                        className="cancel-btn" 
                        onClick={() => setCancellingOrderId(order._id)}
                        style={{ marginRight: '10px' }}
                      >
                        Cancel Order
                      </button>
                    )}
                  </>
                )}

                <button className="track-btn" onClick={() => handleTrackProgress(order._id)}>
                  Track My Order
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;